import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { SR_API_ACTION_UTILITY_PARTNER_SELF_ONBOARDING_STEP_UPDATE } from 'api/utilityActionTypes';

import {
  UTILITY_SELF_ONBOARDING_STEP_USER,
  UTILITY_SELF_ONBOARDING_STEP_BRAND,
  UTILITY_SELF_ONBOARDING_STEP_SYNC,
  UTILITY_SELF_ONBOARDING_STEP_CONNECT,
  UTILITY_SELF_ONBOARDING_STEP_EMAIL,
  UTILITY_SELF_ONBOARDING_STEP_INTRO,
  UTILITY_SELF_ONBOARDING_STEP_INVITE_TEAM,
} from 'common/utility/constants';

import { hasRoleAdmin } from 'common/utility/userRoles';

import branch from 'client/core/branch';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

import UpSelfOnboardingUserStep from 'client/components/UPSelfOnboarding/UpSelfOnboardingUserStep';
import UpSelfOnboardingSyncStep from 'client/components/UPSelfOnboarding/UpSelfOnboardingSyncStep';
import UpSelfOnboardingConnectStep from 'client/components/UPSelfOnboarding/UpSelfOnboardingConnectStep';
import UpSelfOnboardingEmailStep from 'client/components/UPSelfOnboarding/UpSelfOnboardingEmailStep';
import UpSelfOnboardingIntroStep from 'client/components/UPSelfOnboarding/UpSelfOnboardingIntroStep';
import UpSelfOnboardingBrandStep from 'client/components/UPSelfOnboarding/UpSelfOnboardingBrandStep';
import UpSelfOnboardingInviteTeamStep from 'client/components/UPSelfOnboarding/UpSelfOnboardingInviteTeamStep';

import './UpSelfOnboarding.less';

const UP_SELF_ONBOARDING_STEPS = [
  UTILITY_SELF_ONBOARDING_STEP_USER,
  UTILITY_SELF_ONBOARDING_STEP_BRAND,
  UTILITY_SELF_ONBOARDING_STEP_SYNC,
  UTILITY_SELF_ONBOARDING_STEP_INVITE_TEAM,
  UTILITY_SELF_ONBOARDING_STEP_CONNECT,
  UTILITY_SELF_ONBOARDING_STEP_EMAIL,
  UTILITY_SELF_ONBOARDING_STEP_INTRO,
];

const UP_SELF_ONBOARDING_STEP_TO_COMPONENT = {
  [UTILITY_SELF_ONBOARDING_STEP_USER]: UpSelfOnboardingUserStep,
  [UTILITY_SELF_ONBOARDING_STEP_BRAND]: UpSelfOnboardingBrandStep,
  [UTILITY_SELF_ONBOARDING_STEP_SYNC]: UpSelfOnboardingSyncStep,
  [UTILITY_SELF_ONBOARDING_STEP_INVITE_TEAM]: UpSelfOnboardingInviteTeamStep,
  [UTILITY_SELF_ONBOARDING_STEP_CONNECT]: UpSelfOnboardingConnectStep,
  [UTILITY_SELF_ONBOARDING_STEP_EMAIL]: UpSelfOnboardingEmailStep,
  [UTILITY_SELF_ONBOARDING_STEP_INTRO]: UpSelfOnboardingIntroStep,
};

const getInitialStep = ({ utilityPartner }) => {
  if (!utilityPartner) {
    return UTILITY_SELF_ONBOARDING_STEP_USER;
  }

  if (!utilityPartner.logoUrl) {
    return UTILITY_SELF_ONBOARDING_STEP_BRAND;
  }

  if (
    !utilityPartner.selfOnboardingStepsCompleted?.includes(UTILITY_SELF_ONBOARDING_STEP_INVITE_TEAM)
  ) {
    return UTILITY_SELF_ONBOARDING_STEP_INVITE_TEAM;
  }

  if (!utilityPartner.listingFeeds.length) {
    return UTILITY_SELF_ONBOARDING_STEP_SYNC;
  }

  if (!utilityPartner.propertyManagementSoftwareId) {
    return UTILITY_SELF_ONBOARDING_STEP_CONNECT;
  }

  if (utilityPartner.selfOnboardingStepsCompleted.includes(UTILITY_SELF_ONBOARDING_STEP_EMAIL)) {
    return UTILITY_SELF_ONBOARDING_STEP_INTRO;
  }

  return UTILITY_SELF_ONBOARDING_STEP_EMAIL;
};

@branch()
class UpSelfOnboarding extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: getInitialStep({ utilityPartner: props.utilityPartner }),
    };
  }

  saveSelfOnboardingStep = async () => {
    const { dispatch, utilityPartner, onComplete } = this.props;
    const { currentStep } = this.state;

    return dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_SELF_ONBOARDING_STEP_UPDATE,
      payload: {
        utilityPartnerId: utilityPartner.id,
        selfOnboardingStep: currentStep,
      },
      onSuccess: () => {
        const nextStepIndex = UP_SELF_ONBOARDING_STEPS.indexOf(currentStep) + 1;
        const nextStep = UP_SELF_ONBOARDING_STEPS[nextStepIndex];

        if (!nextStep) {
          onComplete();
        }

        return dispatch(UtilityPartnerActions.getBySlug, {
          slug: utilityPartner.slug,
          forceRefresh: true,
        });
      },
    });
  };

  onNext = () => {
    const { onComplete } = this.props;
    const { currentStep } = this.state;

    this.saveSelfOnboardingStep().then(() => {
      const nextStepIndex = UP_SELF_ONBOARDING_STEPS.indexOf(currentStep) + 1;
      const nextStep = UP_SELF_ONBOARDING_STEPS[nextStepIndex];

      if (nextStep) {
        this.setState({ currentStep: nextStep });
      } else {
        onComplete();
      }
    });
  };

  onSkipStep = () => {
    const { onSkip } = this.props;
    const { currentStep } = this.state;

    const nextStepIndex = UP_SELF_ONBOARDING_STEPS.indexOf(currentStep) + 1;
    const nextStep = UP_SELF_ONBOARDING_STEPS[nextStepIndex];

    if (nextStep) {
      this.setState({ currentStep: nextStep });
    } else {
      onSkip();
    }
  };

  render() {
    const {
      referrer,
      referrerUserId,
      emailToActivate,
      utilityUser,
      utilityPartner,
      utilityPartnerGroup,
      propertyManagementSoftwareList,
    } = this.props;
    const { currentStep } = this.state;

    const UpSelfOnboardingComponent = UP_SELF_ONBOARDING_STEP_TO_COMPONENT[currentStep];
    const activeIndex = UP_SELF_ONBOARDING_STEPS.indexOf(currentStep);

    return (
      <>
        <UpSelfOnboardingComponent
          referrer={referrer}
          activeIndex={activeIndex}
          referrerUserId={referrerUserId}
          emailToActivate={emailToActivate}
          utilityPartnerGroup={utilityPartnerGroup}
          onNext={this.onNext}
          utilityPartner={utilityPartner}
          utilityUser={utilityUser}
          propertyManagementSoftwareList={propertyManagementSoftwareList}
        />
        {utilityUser && hasRoleAdmin(utilityUser.roles) && (
          <div styleName="adminButton" onClick={this.onSkipStep}>
            Skip
          </div>
        )}
      </>
    );
  }
}

UpSelfOnboarding.propTypes = {
  referrer: PropTypes.object,
  referrerUserId: PropTypes.string,
  emailToActivate: PropTypes.string,
  utilityUser: PropTypes.object,
  utilityPartner: PropTypes.object,
  utilityPartnerGroup: PropTypes.object,
  propertyManagementSoftwareList: PropTypes.array,
  onSkip: PropTypes.func,
  onComplete: PropTypes.func,
};

UpSelfOnboarding.displayName = 'UpSelfOnboarding';

export default UpSelfOnboarding;
